"use client";

const sizes = {
  sm: "px-3 py-1.5 text-xs rounded-md",
  md: "px-4 py-2 text-sm rounded-lg",
  lg: "px-5 py-2.5 text-base rounded-xl !lg-w-1/3",
};

const variants = {
  primary: "btn-primary",
  secondary: "btn-secondary",
  ghost: `
    text-blue-600 hover:bg-blue-100 
    dark:text-blue-400 dark:hover:bg-blue-900/30
  `,
  white: `
    bg-white text-blue-600 border 
    hover:bg-gray-100
    dark:bg-gray-100 dark:text-blue-700
  `,
  danger: `
    bg-red-500 text-white 
    hover:bg-red-600 
    focus:ring-red-400
    dark:bg-red-600 dark:hover:bg-red-500
  `,
};

export default function PrimaryButton({ children, variant = "primary", size = "md", className = "", icon, href, disabled = false, target = "_self", loading = false, ...props }) {
  const base =
    "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer";

  const classes = [base, sizes[size], variants[variant], className].join(" ");

  const spinner = (
    <svg
      className={`animate-spin flex-shrink-0 text-current ${size === "sm" ? "h-3.5 w-3.5" : size === "lg" ? "h-5 w-5" : "h-4 w-4"}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );

  const content = (
    <>
      <span className="btn-sweep" aria-hidden="true" />

      <span className="relative z-10 flex items-center gap-2">
        {loading && !icon && spinner}
        {children}
        {loading && icon ? spinner : icon && <span className="flex-shrink-0">{icon}</span>}
      </span>
    </>
  );

  if (href && !loading) {
    return (
      <>
        <ButtonStyles />

        <a href={href} className={classes} target={target}>
          {content}
        </a>
      </>
    );
  }

  return (
    <>
      <ButtonStyles />

      <button className={classes} disabled={disabled || loading} {...props}>
        {content}
      </button>
    </>
  );
}

function ButtonStyles() {
  return (
    <style>{`


      .btn-primary {
        position: relative;
        overflow: hidden;

        background: linear-gradient(
          90deg,
          #8f2dff 0%,
          #7b3fff 22%,
          #5b5fff 48%,
          #3d7dff 74%,
          #1da1ff 100%
        );

        color: #fff;
        border: none;

        box-shadow: 0 0 20px rgba(91, 95, 255, 0.35);

        z-index: 0;

        transition:
          opacity 0.25s ease,
          transform 0.25s ease,
          box-shadow 0.25s ease;
      }


      .btn-primary .btn-sweep {
        position: absolute;
        inset: 0;

        background: linear-gradient(
          90deg,
          #1da1ff 0%,
          #3d7dff 26%,
          #5b5fff 52%,
          #7b3fff 78%,
          #8f2dff 100%
        );

        transform: translateX(-101%);
        transition: transform 0.46s cubic-bezier(0.4, 0, 0.2, 1) 0.04s;

        z-index: 1;
        border-radius: inherit;
      }

      .btn-primary:hover .btn-sweep,
      .btn-primary:focus-visible .btn-sweep {
        transform: translateX(0%);
      }

      .btn-primary:hover,
      .btn-primary:focus-visible {
        opacity: 0.95;
        box-shadow: 0 0 20px rgba(91, 95, 255, 0.35);
        color: #fff;
        transform: translateY(-1px);
      }

      .btn-primary > *:not(.btn-sweep) {
        position: relative;
        z-index: 2;
      }



      .btn-secondary {
        position: relative;
        overflow: hidden;

        background: transparent;
        color: var(linear-gradient(90deg,#8F2DFF_0%,#7B3FFF_22%,#5B5FFF_48%,#3D7DFF_74%,#1DA1FF_100%));

        border: none;
        box-shadow: inset 0 0 0 1.5px var(--c-brand, #3b5bdb);

        transition:
          color 0.42s ease,
          box-shadow 0.42s ease;

        z-index: 0;
      }

      .btn-secondary .btn-sweep {
        position: absolute;
        inset: 0;

        background: linear-gradient(
          90deg,
          #8f2dff 0%,
          #7b3fff 22%,
          #5b5fff 48%,
          #3d7dff 74%,
          #1da1ff 100%
        );

        transform: translateX(-101%);
        transition: transform 0.46s cubic-bezier(0.4, 0, 0.2, 1) 0.04s;

        z-index: -1;
        border-radius: inherit;
      }

      .btn-secondary:hover .btn-sweep,
      .btn-secondary:focus-visible .btn-sweep {
        transform: translateX(0%);
      }

      .btn-secondary:hover,
      .btn-secondary:focus-visible {
        color: #fff;
        box-shadow: inset 0 0 0 1.5px transparent;
      }

      .btn-secondary > *:not(.btn-sweep) {
        position: relative;
        z-index: 1;
        transition: color 0.28s ease 0.12s;
      }

      .btn-secondary:not(:hover):not(:focus-visible)
        > *:not(.btn-sweep) {
        transition: color 0.22s ease;
      }

 

      [data-theme="dark"] .btn-primary {
        box-shadow: 0 0 24px rgba(91, 95, 255, 0.45);
      }

      [data-theme="dark"] .btn-primary:hover {
        box-shadow: 0 0 28px rgba(91, 95, 255, 0.55);
      }

      [data-theme="dark"] .btn-secondary {
        color: var(--c-brand-lt, #748ffc);
        box-shadow: inset 0 0 0 1.5px var(--c-brand-lt, #748ffc);
      }

      [data-theme="dark"] .btn-secondary:hover,
      [data-theme="dark"] .btn-secondary:focus-visible {
        color: #fff;
        box-shadow: inset 0 0 0 1.5px transparent;
      }
    `}</style>
  );
}
